import {Injectable} from '@angular/core';
import {CameraEnhancer, CameraView, CaptureVisionRouter, DecodedBarcodesResult} from 'dynamsoft-barcode-reader-bundle';
import {DynavsoftConfigService} from './dynamsoft-config.service';
import {environment} from '../../../../environments/environment';
import {DynamsoftLicenceNotificationService} from './dynamsoft-licence-notification.service';

/**
 * Service for managing camera pre-loading and Dynamsoft initialization
 * to improve scanning performance by starting camera before user interaction
 */
@Injectable({
  providedIn: 'root',
})
export class CameraPreloadService {
  private cameraEnhancer: CameraEnhancer | null = null;
  private cameraView: CameraView | null = null;
  private captureVisionRouter: CaptureVisionRouter | null = null;
  private hiddenElement: HTMLDivElement | null = null;

  private isPreloading = false;
  private isPreloaded = false;
  private preloadPromise: Promise<void> | null = null;

  constructor(
    private dynavsoftConfigService: DynavsoftConfigService,
    private readonly dynamsoftLicenceNotificationService: DynamsoftLicenceNotificationService,
  ) {
  }

  /**
   * Check if camera is already preloaded and ready
   */
  get isReady(): boolean {
    return this.isPreloaded && this.cameraEnhancer && this.cameraView && !!this.captureVisionRouter;
  }

  /**
   * Get preloaded camera instances
   */
  getPreloadedInstances(): {
    cameraEnhancer: CameraEnhancer | null;
    cameraView: CameraView | null;
    captureVisionRouter: CaptureVisionRouter | null;
  } {
    return {
      cameraEnhancer: this.cameraEnhancer,
      cameraView: this.cameraView,
      captureVisionRouter: this.captureVisionRouter,
    };
  }

  /**
   * Start preloading camera in background
   * @param hiddenElement - Hidden HTML element for camera preview
   */
  async startPreloading(hiddenElement: HTMLDivElement): Promise<void> {
    // Prevent multiple preloading attempts
    if (this.isPreloading || this.isPreloaded) {
      const promise = this.preloadPromise;
      if (promise !== null) {
        await promise;
      }
      return;
    }

    this.isPreloading = true;
    this.hiddenElement = hiddenElement;

    this.preloadPromise = this.initializeCamera();

    try {
      await this.preloadPromise;
      this.isPreloaded = true;
    } catch (error) {
      console.error('Camera preloading failed:', error);
      await this.dynamsoftLicenceNotificationService.notifyIfLicenceError(error);
      this.cleanup();
    } finally {
      this.isPreloading = false;
    }

    return this.preloadPromise;
  }

  /**
   * Initialize Dynamsoft camera components
   */
  private async initializeCamera(): Promise<void> {
    try {
      // Initialize Dynamsoft (will be configured with license key)
      const licenseKey = environment.dynamsoft.apiKey;
      await this.dynavsoftConfigService.initializeDynamsoft(licenseKey);

      if (!this.hiddenElement) {
        throw new Error('Hidden element not available');
      }
      this.cameraView = await CameraView.createInstance(this.hiddenElement);

      this.cameraEnhancer = await CameraEnhancer.createInstance(this.cameraView);

      this.captureVisionRouter = await CaptureVisionRouter.createInstance();

      this.captureVisionRouter.setInput(this.cameraEnhancer);
      await this.cameraEnhancer.open();
    } catch (error) {
      console.error('Failed to preload camera:', error);
      throw error;
    }
  }

  /**
   * Transfer preloaded camera to visible modal element
   * @param visibleElement - The modal's visible camera element
   */
  async transferCameraToModal(visibleElement: HTMLDivElement): Promise<void> {
    if (!this.isReady) {
      throw new Error('Camera not preloaded. Cannot transfer to modal.');
    }

    try {
      // Dispose the old camera view
      if (this.cameraView) {
        this.cameraView.dispose();
      }

      // Create new camera view with visible element
      this.cameraView = await CameraView.createInstance(visibleElement);

      // Update camera enhancer with new view
      if (this.cameraEnhancer) {
        try {
          this.cameraEnhancer.close();
          this.cameraEnhancer.dispose();
        } catch (error) {
          console.warn('Error disposing old camera enhancer:', error);
        }
      }

      this.cameraEnhancer = await CameraEnhancer.createInstance(this.cameraView);

      // Update capture vision router
      if (this.captureVisionRouter) {
        this.captureVisionRouter.setInput(this.cameraEnhancer);
      }

      // Reopen camera with new view
      await this.cameraEnhancer.open();

    } catch (error) {
      console.error('Failed to transfer camera to modal:', error);
      throw error;
    }
  }

  /**
   * Start capturing with barcode detection
   * @param onBarcodeDetected - Callback for when barcode is detected
   */
  async startCapturing(onBarcodeDetected: (result: DecodedBarcodesResult) => void): Promise<void> {
    if (!this.captureVisionRouter) {
      throw new Error('Capture vision router not available');
    }

    // Add result receiver
    this.captureVisionRouter.addResultReceiver({
      onDecodedBarcodesReceived: onBarcodeDetected,
    });

    // Start capturing
    await this.captureVisionRouter.startCapturing('ReadBarcodes_Balance');
  }

  /**
   * Stop capturing barcodes
   */
  stopCapturing(): void {
    if (this.captureVisionRouter) {
      try {
        this.captureVisionRouter.stopCapturing();
      } catch (error) {
        console.warn('Error stopping capture:', error);
      }
    }
  }

  /**
   * Toggle torch on/off
   */
  toggleTorch(on: boolean): void {
    if (this.cameraEnhancer) {
      try {
        if (on) {
          this.cameraEnhancer.turnOnTorch().catch((error: unknown) => {
            console.warn('Error turning on torch:', error);
          });
        } else {
          this.cameraEnhancer.turnOffTorch().catch((error: unknown) => {
            console.warn('Error turning off torch:', error);
          });
        }
      } catch (error) {
        console.warn('Error toggling torch:', error);
      }
    }
  }

  /**
   * Get available cameras
   */
  async getAvailableCameras(): Promise<MediaDeviceInfo[]> {
    if (!this.cameraEnhancer) {
      return [];
    }

    try {
      const cameras = await this.cameraEnhancer.getAllCameras();
      // Convert VideoDeviceInfo[] to MediaDeviceInfo[] for compatibility
      return cameras.map(camera => ({
        deviceId: camera.deviceId,
        groupId: '',
        kind: 'videoinput' as const,
        label: camera.label,
        toJSON: () => ({
          deviceId: camera.deviceId,
          groupId: '',
          kind: 'videoinput',
          label: camera.label,
        }),
      }));
    } catch (error) {
      console.warn('Error getting cameras:', error);
      return [];
    }
  }

  /**
   * Cleanup all camera resources
   */
  cleanup(): void {
    this.stopCapturing();

    if (this.captureVisionRouter) {
      try {
        this.captureVisionRouter.dispose().catch((error: unknown) => {
          console.warn('Error disposing capture vision router:', error);
        });
      } catch (error) {
        console.warn('Error disposing capture vision router:', error);
      }
      this.captureVisionRouter = null;
    }

    if (this.cameraEnhancer) {
      try {
        this.cameraEnhancer.close();
        this.cameraEnhancer.dispose();
      } catch (error) {
        console.warn('Error disposing camera enhancer:', error);
      }
      this.cameraEnhancer = null;
    }

    if (this.cameraView) {
      try {
        this.cameraView.dispose();
      } catch (error) {
        console.warn('Error disposing camera view:', error);
      }
      this.cameraView = null;
    }

    this.isPreloaded = false;
    this.isPreloading = false;
    this.preloadPromise = null;
    this.hiddenElement = null;
  }
}
